/*
 * Client-side "contains" search.
 * Shopify's native search only matches from the start of a word, so "14" never finds SKU "FS14".
 * This builds a small index from /products.json (title, SKUs, description, vendor, type, tags)
 * and matches the search term anywhere inside those fields.
 */
(function () {
  if (window.CstmSearchIndex) return;

  const CACHE_KEY = 'cstm-search-index-v2';
  const CACHE_TTL = 30 * 60 * 1000; // 30 minutes
  const PAGE_LIMIT = 250;
  const MAX_PAGES = 40;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let indexPromise = null;
  const decoder = document.createElement('textarea');

  function normalize(text) {
    return (text || '').toString().toLowerCase();
  }

  function compact(text) {
    return normalize(text).replace(/[^a-z0-9]/g, '');
  }

  function stripHtml(html) {
    decoder.innerHTML = (html || '').replace(/<[^>]*>/g, ' ');
    return decoder.value.replace(/\s+/g, ' ').trim();
  }

  function buildEntry(product) {
    const skus = (product.variants || []).map((variant) => variant.sku).filter(Boolean);
    const image = product.images && product.images[0] ? product.images[0].src : '';
    const tags = Array.isArray(product.tags) ? product.tags.join(' ') : product.tags || '';

    return {
      h: product.handle,
      p: Math.min(...(product.variants || []).map((variant) => parseFloat(variant.price) || 0), Infinity) || 0,
      t: product.title,
      s: skus,
      i: image,
      title: normalize(product.title),
      sku: normalize(skus.join(' ')),
      skuCompact: skus.map(compact).join(' '),
      meta: normalize([product.vendor, product.product_type, tags].join(' ')),
      desc: normalize(stripHtml(product.body_html)),
    };
  }

  function readCache() {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (cached && Date.now() - cached.time < CACHE_TTL && Array.isArray(cached.items)) return cached.items;
    } catch (e) {}
    return null;
  }

  function writeCache(items) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), items }));
    } catch (e) {}
  }

  async function fetchAll() {
    const items = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
      const response = await fetch(`${root}products.json?limit=${PAGE_LIMIT}&page=${page}`);
      if (!response.ok) throw new Error(response.status);
      const data = await response.json();
      const products = data.products || [];
      products.forEach((product) => items.push(buildEntry(product)));
      if (products.length < PAGE_LIMIT) break;
    }
    return items;
  }

  function load() {
    if (!indexPromise) {
      const cached = readCache();
      indexPromise = cached
        ? Promise.resolve(cached)
        : fetchAll().then((items) => {
            writeCache(items);
            return items;
          });
      indexPromise.catch(() => {
        indexPromise = null;
      });
    }
    return indexPromise;
  }

  function scoreEntry(entry, words, compactTerm) {
    let score = 0;
    for (const word of words) {
      const compactWord = compact(word);
      if (entry.s.some((sku) => normalize(sku) === word)) score += 100;
      else if (entry.sku.includes(word) || (compactWord && entry.skuCompact.includes(compactWord))) score += 50;
      else if (entry.title.includes(word)) score += 30;
      else if (entry.meta.includes(word)) score += 10;
      else if (entry.desc.includes(word)) score += 5;
      else return 0; // every word must appear somewhere
    }
    // Bonus when the whole term (ignoring spaces/dashes) is inside a SKU, e.g. "fs 14" -> "FS14"
    if (compactTerm && entry.skuCompact.includes(compactTerm)) score += 25;
    return score;
  }

  // Approximates Shopify's own matching (every word is the start of some token),
  // so callers can tell which matches Shopify already returned.
  function nativeMatch(entry, words) {
    const tokens = [entry.title, entry.sku, entry.meta, entry.desc].join(' ').split(/[^a-z0-9]+/);
    return words.every((word) => {
      const parts = word.split(/[^a-z0-9]+/).filter(Boolean);
      return parts.every((part) => tokens.some((token) => token.startsWith(part)));
    });
  }

  function matchingSku(entry, words) {
    return entry.s.find((sku) => words.some((word) => normalize(sku).includes(word))) || entry.s[0] || '';
  }

  async function find(term, options = {}) {
    const words = normalize(term).trim().split(/\s+/).filter(Boolean);
    if (!words.length) return [];

    const items = await load();
    const compactTerm = compact(term);
    const results = [];

    items.forEach((entry) => {
      const score = scoreEntry(entry, words, compactTerm);
      if (!score) return;
      if (options.onlyNonNative && nativeMatch(entry, words)) return;
      results.push({
        handle: entry.h,
        title: entry.t,
        image: entry.i,
        sku: matchingSku(entry, words),
        url: `${root}products/${entry.h}`,
        score,
        price: entry.p,
      });
    });

    results.sort((a, b) => b.score - a.score);
    return options.limit ? results.slice(0, options.limit) : results;
  }

  window.CstmSearchIndex = { load, find };
})();
